import React, { useState, Suspense, lazy } from "react"
import { Link } from "react-router-dom"
import SEO from "../components/SEO.jsx"
import Hero from "../components/Hero.jsx"
import PackagesList from "../components/PackagesList.jsx"
import AddOnsPortal from "../components/AddOnsPortal.jsx"
import PackagesPortal from "../components/PackagesPortal.jsx"
import InlineHealthCheckCTA from "../components/InlineHealthCheckCTA.jsx"
import GoogleProfileReviewCTA from "../components/GoogleProfileReviewCTA.jsx"
import HomeLandingLinks from "../components/HomeLandingLinks.jsx"

const AddOns = lazy(() => import("../components/AddOns.jsx"))

export default function Home() {
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [selectedAddOn, setSelectedAddOn] = useState(null)

  const aboutLines = [
    "2merative is an Adelaide web design studio led by Jason. We build websites that feel calm, clear, and credible, and that make it easy for customers to take the next step.",
    "We treat your website like business infrastructure. Solid structure. Clean content hierarchy. Clear conversion paths. Built to scale as your business grows.",
    "Our approach to web design in Adelaide is practical. No hype. No jargon. Just thoughtful design and tidy implementation that improves clarity and protects your brand.",
    "Whether you are updating an outdated website or building a stronger platform for growth, we help Adelaide businesses present with confidence and perform consistently online.",
  ]

  const trustPoints = [
    {
      title: "Clear structure",
      text: "Websites built with clean hierarchy, strong calls to action, and a layout that helps people find what matters quickly.",
    },
    {
      title: "Performance-focused",
      text: "Fast, mobile-friendly builds designed to reduce friction, support visibility, and create a smoother customer journey.",
    },
    {
      title: "Local support",
      text: "Adelaide-based support with practical advice, straightforward communication, and websites designed around real business goals.",
    },
    {
      title: "Connected systems",
      text: "We connect your website with the CRM, booking, email and administration tools you use, reducing duplicated work and keeping information flowing to the right place.",
    },
  ]

  const workflowOutcomes = [
    {
      title: "Fewer manual steps",
      text: "Stop copying enquiries, bookings and customer details between different systems.",
    },
    {
      title: "Connected business tools",
      text: "Connect forms, CRM platforms, booking systems, email marketing, Airtable and other services.",
    },
    {
      title: "One reliable source of truth",
      text: "Update information once and use it wherever your business needs it.",
    },
  ]

  return (
    <div className="bg-white text-gray-800">
      <SEO
        title="Web Design Adelaide | Affordable Business Websites | 2merative"
        description="Web design Adelaide services for businesses ready to grow. Affordable, fast, and conversion-focused websites built to perform."
        path="/"
        image="/og-image.png"
      />

      <Hero />

      <section className="max-w-5xl mx-auto px-6 py-20 text-center min-h-[500px]">
        <h2 className="text-4xl font-bold text-blue-900 mb-10">
          Web Design Adelaide That Works for Your Business
        </h2>

        {aboutLines.map((line, index) => (
          <p
            key={index}
            className="text-lg text-gray-800 mb-4 mx-auto leading-relaxed min-h-[80px] max-w-[700px]"
          >
            {line}
          </p>
        ))}

        <p className="mt-6">
          <Link
            to="/web-design-adelaide"
            className="text-blue-700 font-medium hover:underline"
          >
            Learn more about our web design Adelaide services →
          </Link>
        </p>
      </section>

      <section
        id="packages"
        className="py-20 bg-gradient-to-br from-white to-blue-50 rounded-3xl border border-blue-100 shadow-lg max-w-6xl mx-auto px-6"
      >
        <h2 className="text-4xl font-bold text-blue-900 mb-12 text-center">
          Website Design Packages
        </h2>
        <PackagesList limit={3} onPackageClick={setSelectedPackage} />

        <div className="text-center mt-8">
          <Link
            to="/website-landing"
            className="text-blue-700 font-semibold hover:underline"
          >
            View all website packages →
          </Link>
        </div>
      </section>

      <section className="py-20 max-w-6xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-6">
          Website Add-Ons and Growth Options
        </h2>
        <p className="text-center text-gray-600 mb-10 text-sm sm:text-base">
          Extend your website with SEO, optimisation, and performance upgrades
        </p>

        <Suspense
          fallback={<div className="text-center text-gray-400">Loading add-ons…</div>}
        >
          <AddOns onAddOnClick={setSelectedAddOn} />
        </Suspense>
      </section>

      <section className="py-20 max-w-6xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 mb-3">
            Beyond website design
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
            A website that improves how your business works
          </h2>
          <p className="text-gray-700">
            Your website should do more than look professional. It can connect the tools your
            business already uses, reduce repetitive administration and help information move
            automatically to the right place.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {workflowOutcomes.map((point) => (
            <article
              key={point.title}
              className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm"
            >
              <h3 className="text-xl font-semibold text-blue-900 mb-3">
                {point.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {point.text}
              </p>
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/contact"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:bg-blue-700 transition duration-300"
          >
            Let’s discuss your workflow
          </Link>
          <p className="text-sm text-gray-600 mt-4 max-w-xl mx-auto">
            Tell us what currently takes too much time, requires double handling or regularly
            falls through the gaps.
          </p>
        </div>
      </section>

      <section className="py-16 bg-orange-50 border-y border-orange-200 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
          Improve Your Google Visibility in Adelaide
        </h2>
        <p className="text-gray-700 mb-6 max-w-xl mx-auto">
          One-time Google Business Profile optimisation to improve local search visibility, trust,
          and enquiries.
        </p>
        <GoogleProfileReviewCTA className="mx-auto" />
      </section>

      <InlineHealthCheckCTA />

      <HomeLandingLinks showGroups={false} />

      <section className="py-20 max-w-6xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
            Built for clarity, trust, and growth
          </h2>
          <p className="text-gray-700">
            Good web design is not just about appearance. It should help your business look
            credible, communicate clearly, and convert attention into action.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {trustPoints.map((point) => (
            <article
              key={point.title}
              className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm"
            >
              <h3 className="text-xl font-semibold text-blue-900 mb-3">
                {point.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {point.text}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="py-16 max-w-5xl mx-auto px-6">
        <div className="rounded-3xl border border-blue-100 bg-slate-50 p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-4">
            Adelaide web design with local support
          </h2>
          <p className="text-gray-700 max-w-2xl mx-auto mb-6">
            2merative provides web design Adelaide services for businesses that need a website
            built with clarity, performance, and long-term usability in mind.
          </p>

          <div className="grid gap-4 sm:grid-cols-3 text-sm text-gray-700 max-w-3xl mx-auto">
            <div className="rounded-2xl bg-white border border-blue-100 p-4">
              <p className="font-semibold text-blue-900 mb-1">Location</p>
              <p>Adelaide, South Australia</p>
            </div>
            <div className="rounded-2xl bg-white border border-blue-100 p-4">
              <p className="font-semibold text-blue-900 mb-1">Service Area</p>
              <p>Adelaide and surrounding areas</p>
            </div>
            <div className="rounded-2xl bg-white border border-blue-100 p-4">
              <p className="font-semibold text-blue-900 mb-1">Contact</p>
              <Link to="/contact" className="text-blue-700 hover:underline">
                Contact 2merative
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-blue-50 to-blue-100 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-blue-900">
          Ready to upgrade your website?
        </h2>
        <p className="mb-6 text-gray-700">
          Talk to a local Adelaide web design partner who understands how to build for performance
          and growth.
        </p>
        <Link
          to="/contact"
          className="inline-block bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-blue-700 transition duration-300"
        >
          Get in Touch
        </Link>
      </section>

      {selectedPackage && (
        <PackagesPortal
          data={selectedPackage}
          onClose={() => setSelectedPackage(null)}
        />
      )}
      {selectedAddOn && (
        <AddOnsPortal
          data={selectedAddOn}
          onClose={() => setSelectedAddOn(null)}
        />
      )}
    </div>
  )
}
